import { useCallback, useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Entity } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";

import { PathParams } from "@/services/api";
import { selectUpdateEntity, selectUpdateState } from "@/store/crud";
import {
  loadUpdateItem,
  resetUpdate,
  updateItem as updateItemAction
} from "@/store/crud/update";
import { clearFocusedEntity } from "@/store/navigation/entityNavigation";
import { performanceMonitor } from "@/utils/performance";

const useEntityUpdate = <E extends Entity = Entity>(
  dataType: DataType,
  id: string,
  returnPath?: string,
  pathParams?: PathParams
) => {
  const dispatch = useDispatch();
  const hookInstanceId = useMemo(
    () =>
      `${dataType}-${id}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    [dataType, id]
  );

  useEffect(() => {
    const metricName = "Hook.useEntityUpdate.initialLoadEffect";
    const initialContext = { hookInstanceId, dataType, id, pathParams };
    const metricId = performanceMonitor.start(metricName, initialContext);
    dispatch(loadUpdateItem(dataType, id, pathParams));
    performanceMonitor.stop(metricId);

    return () => {
      const cleanupMetricName = "Hook.useEntityUpdate.cleanupEffect";
      const cleanupContext = { hookInstanceId, dataType };
      const cleanupMetricId = performanceMonitor.start(
        cleanupMetricName,
        cleanupContext
      );
      dispatch(resetUpdate(dataType));
      dispatch(clearFocusedEntity());
      performanceMonitor.stop(cleanupMetricId);
    };
  }, [dispatch, dataType, id, pathParams, hookInstanceId]);

  const itemDataSelector = useMemo(
    () => selectUpdateEntity(dataType),
    [dataType]
  );
  const itemData = useSelector(itemDataSelector) as E | undefined;

  const updateStateSelector = useMemo(
    () => selectUpdateState(dataType),
    [dataType]
  );
  const updateState = useSelector(updateStateSelector);

  const updateItem = useCallback(
    (changes: Partial<E>) => {
      dispatch(
        updateItemAction(
          dataType,
          id,
          { ...changes } as Entity,
          undefined,
          undefined,
          returnPath,
          pathParams
        )
      );
    },
    [dispatch, dataType, id, returnPath, pathParams]
  );

  return {
    isLoading: !!updateState?.isLoading,
    isSaving: !!updateState?.isSaving,
    itemData,
    loadingError: updateState?.loadingError,
    savingError: updateState?.savingError,
    updateItem,
    updateState: updateState?.savingError
  };
};

export default useEntityUpdate;
